/**
 * Shared name-formatting utilities.
 *
 * Handles every bad value that can come from the API:
 *   - JS null / undefined
 *   - The literal string "null" or "undefined" stored in the DB
 *   - Blank / whitespace-only strings
 */

const isBlank = (s: string | null | undefined): boolean =>
  !s ||
  s.trim() === "" ||
  s.trim().toLowerCase() === "null" ||
  s.trim().toLowerCase() === "undefined";

/**
 * Join any number of name parts, skipping nulls and the literal string "null".
 *
 * fmtName("Jane", null)      → "Jane"
 * fmtName(null, "Smith")     → "Smith"
 * fmtName(null, null)        → ""
 * fmtName("null", "Dinesh")  → "Dinesh"
 */
export const fmtName = (...parts: (string | null | undefined)[]): string =>
  parts
    .filter((p) => !isBlank(p))
    .map((p) => p!.trim())
    .join(" ");

/**
 * Resolve the display name for a customer/client object.
 *
 * Priority: companyName → firstName + lastName → fallback
 */
export const fmtCustomerName = (
  c:
    | {
        customerCompanyName?: string | null;
        customerFirstName?: string | null;
        customerLastName?: string | null;
      }
    | null
    | undefined,
  fallback = "—"
): string => {
  if (!c) return fallback;
  if (!isBlank(c.customerCompanyName)) return c.customerCompanyName!.trim();
  const full = fmtName(c.customerFirstName, c.customerLastName);
  return full || fallback;
};

/**
 * Resolve the display name for a driver object.
 */
export const fmtDriverName = (
  d:
    | {
        driverFirstName?: string | null;
        driverLastName?: string | null;
      }
    | null
    | undefined,
  fallback = "—"
): string => {
  if (!d) return fallback;
  return fmtName(d.driverFirstName, d.driverLastName) || fallback;
};
